import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, resolveAudio } from "@/lib/api";

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [station, setStation] = useState(null);     // {station_id, name, ...}
  const [current, setCurrent] = useState(null);     // current track
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState("");           // user-visible error message
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [listenerId] = useState(() => {
    const k = "fliboe_listener_id";
    let v = localStorage.getItem(k);
    if (!v) { v = "lst_" + Math.random().toString(36).slice(2, 12); localStorage.setItem(k, v); }
    return v;
  });
  const lastTrackIdRef = useRef(null);
  const syncTimerRef = useRef(null);
  const hbTimerRef = useRef(null);
  const failedTrackRef = useRef(null);   // last track that failed → don't retry until server advances
  const [founderDrop, setFounderDrop] = useState(null); // {audio_url, title, subtitle, duration} when playing
  const founderResolveRef = useRef(null);  // resolves when founder drop finishes or is skipped
  const founderPlayingRef = useRef(false); // set TRUE the moment tuneIn decides to play the drop, cleared in finally. Guards sync races.

  // Sync to a station's "now playing"
  const syncStation = useCallback(async (stationId) => {
    // Don't fight the founder drop — let it finish (or be skipped) first.
    if (founderResolveRef.current || founderPlayingRef.current) return;
    try {
      const { data } = await api.get(`/stations/${stationId}/now`);
      if (!data.current) {
        setError("This station has no tracks yet.");
        return;
      }
      const audio = audioRef.current;
      if (!audio) return;
      const url = resolveAudio(data.current.audio_url);
      const isNewTrack = lastTrackIdRef.current !== data.current.track_id;
      if (isNewTrack) {
        // If this is the same track that just failed, don't keep retrying
        if (failedTrackRef.current === data.current.track_id) return;
        lastTrackIdRef.current = data.current.track_id;
        setError("");
        audio.src = url;
        audio.currentTime = data.offset || 0;
        try { await audio.play(); setIsPlaying(true); } catch (_) { /* autoplay blocked */ }
        setCurrent(data.current);
      } else {
        // small drift correction (>3s)
        if (Math.abs((audio.currentTime || 0) - data.offset) > 3) {
          audio.currentTime = data.offset;
        }
      }
    } catch (e) {
      // silent
    }
  }, []);

  const tuneIn = useCallback(async (st) => {
    setStation(st);
    lastTrackIdRef.current = null;
    failedTrackRef.current = null;
    setError("");
    // Play the founder drop once per browser session, before the station feed kicks in.
    // We use sessionStorage so it replays for new sessions but not on every tune.
    try {
      const KEY = "fliboe_founder_played";
      if (!sessionStorage.getItem(KEY)) {
        // Claim the ref IMMEDIATELY so the concurrent syncStation triggered
        // by setStation(st) doesn't race in and set audio.src to a station track.
        founderPlayingRef.current = true;
        const { data } = await api.get("/founder-drop");
        if (data?.audio_url) {
          sessionStorage.setItem(KEY, String(data.version || 1));
          const audio = audioRef.current;
          if (audio) {
            let autoplayOk = false;
            audio.src = resolveAudio(data.audio_url);
            audio.currentTime = 0;
            try { await audio.play(); autoplayOk = true; setIsPlaying(true); } catch (_) { autoplayOk = false; }
            // Only show the banner + await completion if autoplay actually started.
            // Otherwise (blocked by browser), silently fall through to the station feed.
            if (autoplayOk) {
              setFounderDrop({ ...data, audio_url_resolved: resolveAudio(data.audio_url) });
              // Safety: hard cap the await at duration + 4s so we never hang forever.
              const maxWait = Math.max(15000, ((data.duration || 60) + 4) * 1000);
              await new Promise((resolve) => {
                founderResolveRef.current = resolve;
                setTimeout(() => {
                  if (founderResolveRef.current === resolve) resolve();
                }, maxWait);
              });
            }
          }
        }
      }
    } catch (_) {
      // No founder drop generated yet, or fetch failed — silently skip into station feed
    } finally {
      setFounderDrop(null);
      founderResolveRef.current = null;
      founderPlayingRef.current = false;
      // Force syncStation to reset audio.src on the NEXT sync — the audio element
      // is currently pointing at the founder drop URL. Without clearing this ref,
      // syncStation's `isNewTrack` short-circuit skips resetting audio.src and
      // playback would silently die after the drop ends. This is the fix for
      // "stations only play the founder drop" bug.
      lastTrackIdRef.current = null;
    }
    await syncStation(st.station_id);
  }, [syncStation]);

  const skipFounder = useCallback(() => {
    const audio = audioRef.current;
    try { if (audio) { audio.pause(); audio.currentTime = 0; } } catch (_) {}
    if (founderResolveRef.current) founderResolveRef.current();
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !station) return;
    if (audio.paused) {
      // re-sync before resume
      await syncStation(station.station_id);
      try { await audio.play(); setIsPlaying(true); } catch (_) {}
    } else {
      audio.pause(); setIsPlaying(false);
    }
  }, [station, syncStation]);

  // Volume effect
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  // Periodic sync + heartbeat while a station is selected
  useEffect(() => {
    clearInterval(syncTimerRef.current);
    clearInterval(hbTimerRef.current);
    if (!station) return;
    syncTimerRef.current = setInterval(() => syncStation(station.station_id), 8000);
    const sendHeartbeat = async () => {
      try { await api.post(`/stations/${station.station_id}/heartbeat`, { listener_id: listenerId }); } catch (_) {}
    };
    sendHeartbeat();
    hbTimerRef.current = setInterval(sendHeartbeat, 30000);
    return () => {
      clearInterval(syncTimerRef.current);
      clearInterval(hbTimerRef.current);
    };
  }, [station, syncStation, listenerId]);

  // On track end → re-sync (server clock advances). When the founder drop ends,
  // resolve its waiter so the station feed can start.
  const onEnded = () => {
    if (founderResolveRef.current) { founderResolveRef.current(); return; }
    if (station) syncStation(station.station_id);
  };

  // On audio error → if it's the founder drop, just skip it and proceed.
  // Otherwise mark this track broken and re-sync after a moment.
  const onAudioError = () => {
    if (founderResolveRef.current) { founderResolveRef.current(); return; }
    const failed = lastTrackIdRef.current;
    failedTrackRef.current = failed;
    setIsPlaying(false);
    setError("This track is unavailable. The broadcaster needs to re-upload it.");
    // Force the server to consider this as time-elapsed past the broken track:
    // The auto-rotation will naturally land on the NEXT track in 8s. We also try a
    // manual sync after a short delay to confirm.
    if (station) setTimeout(() => syncStation(station.station_id), 8000);
  };

  return (
    <PlayerCtx.Provider value={{ station, current, isPlaying, volume, setVolume, muted, setMuted, tuneIn, togglePlay, audioRef, error, founderDrop, skipFounder }}>
      {children}
      {/* No crossOrigin attr: many free CDN MP3s (e.g. SoundHelix) don't send CORS headers; we don't need AudioContext analysis. */}
      <audio ref={audioRef} onEnded={onEnded} onError={onAudioError} preload="auto" />
    </PlayerCtx.Provider>
  );
}
